/**
 * Turn a picked file into something the editor can use.
 *
 * Downscales and re-encodes in the browser, because the SEO guide caps blog images at 200KB
 * and a phone photo straight off a camera roll is twenty times that. Canvas rather than
 * `@jsquash/webp` for now: jsquash gives better ratios but adds ~900KB to the bundle, and
 * this is the interim before uploads go to the server.
 *
 * ⚠️ The result is a `data:` URL, which is fine for laying a page out but cannot be exported
 * — Webflow would receive the entire image inline in the clipboard payload. The
 * `image-data-url` invariant blocks that, and the field shows the image as "not hosted yet".
 * Real hosting arrives with the upload endpoint.
 */

export interface ProcessedImage {
  dataUrl: string;
  width: number;
  height: number;
  bytes: number;
  filename: string;
  /** The original, before any downscaling — so the field can say what it did. */
  originalBytes: number;
}

export const MAX_EDGE = 1600;
export const TARGET_BYTES = 200_000;

/** Strip the extension and slugify, so `IMG_4523.JPG` becomes something SEO-legible. */
function cleanFilename(name: string, ext: string): string {
  const base = name.replace(/\.[^.]+$/, '');
  const slug =
    base
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'image';
  return `${slug}.${ext}`;
}

function loadBitmap(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('That file could not be read as an image.'));
    };
    img.src = url;
  });
}

function toDataUrl(canvas: HTMLCanvasElement, type: string, quality: number): string {
  return canvas.toDataURL(type, quality);
}

/** Rough byte count of a data URL's payload. */
function dataUrlBytes(dataUrl: string): number {
  const comma = dataUrl.indexOf(',');
  return Math.round(((dataUrl.length - comma - 1) * 3) / 4);
}

export async function processFile(file: File): Promise<ProcessedImage> {
  if (!file.type.startsWith('image/')) {
    throw new Error('That is not an image file.');
  }

  // SVG is already small and rasterising it would be a downgrade.
  if (file.type === 'image/svg+xml') {
    const text = await file.text();
    const dataUrl = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(text)))}`;
    const img = await loadBitmap(file);
    return {
      dataUrl,
      width: img.naturalWidth || 512,
      height: img.naturalHeight || 512,
      bytes: file.size,
      originalBytes: file.size,
      filename: cleanFilename(file.name, 'svg'),
    };
  }

  const img = await loadBitmap(file);

  const scale = Math.min(1, MAX_EDGE / Math.max(img.naturalWidth, img.naturalHeight));
  const width = Math.round(img.naturalWidth * scale);
  const height = Math.round(img.naturalHeight * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not process that image.');
  ctx.drawImage(img, 0, 0, width, height);

  // WebP where the browser supports it — roughly 30% smaller than JPEG at the same quality.
  const supportsWebp = canvas.toDataURL('image/webp').startsWith('data:image/webp');
  const type = supportsWebp ? 'image/webp' : 'image/jpeg';
  const ext = supportsWebp ? 'webp' : 'jpg';

  // Step the quality down until it fits the guide's budget, rather than guessing once.
  let dataUrl = '';
  let bytes = 0;
  for (const quality of [0.82, 0.7, 0.6, 0.5]) {
    dataUrl = toDataUrl(canvas, type, quality);
    bytes = dataUrlBytes(dataUrl);
    if (bytes <= TARGET_BYTES) break;
  }

  return {
    dataUrl,
    width,
    height,
    bytes,
    originalBytes: file.size,
    filename: cleanFilename(file.name, ext),
  };
}

export const isDataUrl = (url: string): boolean => url.startsWith('data:');
